import { useQueryStringParam } from '@/hooks/useQueryStringParam';
import { useCallback, useEffect, useState } from 'react';
import {
  NARRATIVE_COMMUNITY_AFTER_QUERY_PARAM,
  NARRATIVE_COMMUNITY_BEFORE_QUERY_PARAM,
  narrativeListPageParamsSafeParse,
} from '../utils';

type VisibleNarrativesByCursor = {
  [cursor: string]: string[];
};

/**
 * This is a dummy id to indicate that this is a future narrative,
 * it can be anything that doesn't overlap with UUIDs
 */
export const FUTURE_NARRATIVE_ID = 'future';

const removeEmptyAndFutureItems = (
  currentId: string,
  previousState: VisibleNarrativesByCursor
) => {
  return Object.entries(previousState).reduce<VisibleNarrativesByCursor>(
    (result, [key, value]) => {
      if (
        !value.length ||
        (value.includes(FUTURE_NARRATIVE_ID) &&
          currentId === FUTURE_NARRATIVE_ID)
      )
        return result;
      return {
        ...result,
        [key]: value,
      };
    },
    {}
  );
};

/**
 * Finds the most visible cursor and sets it as a query param.
 * It supports bi-directional pagination using after for forward pagination and before for backwards pagination.
 */
export const useNarrativeCommunityQueryParamCursors = () => {
  const { setItem, removeItem, getItem } = useQueryStringParam();
  const [visibleNarrativesByCursor, setVisibleNarrativesByCursor] =
    useState<VisibleNarrativesByCursor>({});

  const initialAfter = getItem(NARRATIVE_COMMUNITY_AFTER_QUERY_PARAM);
  const initialBefore = getItem(NARRATIVE_COMMUNITY_BEFORE_QUERY_PARAM);

  const updateNarrativeVisibility = useCallback(
    ({
      narrativeId,
      after,
      before,
      isVisible,
    }: {
      narrativeId: string;
      after: string | null | undefined;
      before: string | null | undefined;
      isVisible: boolean;
    }) => {
      setVisibleNarrativesByCursor((prev) => {
        const cleanPrevious = removeEmptyAndFutureItems(narrativeId, prev);
        const cursorKey = JSON.stringify({ after, before });
        const visibleNarratives = cleanPrevious[cursorKey] ?? [];

        const updatedVisibleNarratives = isVisible
          ? [...visibleNarratives, narrativeId]
          : visibleNarratives.filter((id) => id !== narrativeId) ?? [];

        return {
          ...cleanPrevious,
          [cursorKey]: updatedVisibleNarratives,
        };
      });
    },
    []
  );

  const mostVisibleCursorStringified = Object.entries(
    visibleNarrativesByCursor
  ).reduce<{
    cursor: string | null;
    visibleNarrativesCount: number;
  }>(
    (result, [cursor, visibleNarratives]) => {
      if (!result || result.visibleNarrativesCount < visibleNarratives.length)
        return {
          cursor,
          visibleNarrativesCount: visibleNarratives.length,
        };

      return result;
    },
    {
      cursor: null,
      visibleNarrativesCount: 0,
    } satisfies {
      cursor: string | null;
      visibleNarrativesCount: number;
    }
  ).cursor;

  const getCurrentCursor = () => {
    if (!mostVisibleCursorStringified)
      return narrativeListPageParamsSafeParse({});

    return narrativeListPageParamsSafeParse(
      JSON.parse(mostVisibleCursorStringified)
    );
  };

  const { after, before } = getCurrentCursor();

  useEffect(() => {
    if (!after) {
      removeItem(NARRATIVE_COMMUNITY_AFTER_QUERY_PARAM);
    } else {
      setItem(NARRATIVE_COMMUNITY_AFTER_QUERY_PARAM, after);
    }
    if (!before) {
      removeItem(NARRATIVE_COMMUNITY_BEFORE_QUERY_PARAM);
    } else {
      setItem(NARRATIVE_COMMUNITY_BEFORE_QUERY_PARAM, before);
    }
  }, [after, before, removeItem, setItem]);

  const resetNarrativeVisibility = useCallback(
    () => setVisibleNarrativesByCursor({}),
    []
  );

  return {
    updateNarrativeVisibility,
    resetNarrativeVisibility,
    initialAfter,
    initialBefore,
  };
};
